import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Loader2, Newspaper, ChevronLeft, ChevronRight } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

const CATS = ['','news','programs','impact','events','health','education','environment','story','announcement'];

export default function BlogPage() {
  const [posts, setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat]       = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(1);
  const [total, setTotal]   = useState(0);
  const PER = 9;

  useEffect(() => {
    setLoading(true);
    api.get(`/blog?limit=${PER}&page=${page}${cat?`&category=${cat}`:''}${search?`&search=${search}`:''}`).then(r => { setPosts(r.data.items||[]); setTotal(r.data.total||0); }).catch(()=>setPosts([])).finally(()=>setLoading(false));
  }, [cat, page, search]);

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">Latest Updates</span>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-5">Blog & News</h1>
            <div className="max-w-md mx-auto relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Search articles..." className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/40 focus:outline-none focus:border-gold-400 transition-colors text-sm" />
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex gap-2 flex-wrap justify-center mb-10">
              {CATS.map(c => (
                <button key={c} onClick={()=>{setCat(c);setPage(1);}}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all capitalize ${cat===c?'bg-royal-800 border-royal-800 text-white':'border-gray-200 text-gray-600 hover:border-royal-300 bg-white'}`}>
                  {c||'All'}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48 gap-2 text-gray-400"><Loader2 size={22} className="animate-spin" />Loading...</div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 text-gray-400"><Newspaper size={40} className="mx-auto mb-3 opacity-30" /><p>No posts found.</p></div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map(post => (
                  <Link key={post._id} to={`/blog/${post._id}`} className="group bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 overflow-hidden border border-gray-100 flex flex-col">
                    <div className="h-48 bg-hero-gradient relative overflow-hidden">
                      {post.image ? <img src={post.thumbImage||post.image} alt={post.title} className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" /> : <div className="flex items-center justify-center h-full"><Newspaper size={40} className="text-white/15" /></div>}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-gold-500 text-royal-950 text-[10px] font-bold rounded-full uppercase tracking-wide capitalize">{post.category||'news'}</span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="text-xs text-gray-400 mb-2">{post.publishedAt?new Date(post.publishedAt).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'}):'Recent'}{post.authorName&&<span className="ml-2">· {post.authorName}</span>}</div>
                      <h3 className="font-display font-semibold text-gray-900 leading-snug mb-2 group-hover:text-royal-800 transition-colors line-clamp-2 flex-1">{post.title}</h3>
                      {post.excerpt && <p className="text-sm text-gray-500 line-clamp-2 mb-3">{post.excerpt}</p>}
                      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal-800 group-hover:gap-2.5 transition-all mt-auto">Read more <ArrowRight size={14} /></div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {total > PER && (
              <div className="flex items-center justify-center gap-3 mt-10">
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={18}/></button>
                <span className="text-sm text-gray-600 font-medium">Page {page} of {Math.ceil(total/PER)}</span>
                <button onClick={()=>setPage(p=>p+1)} disabled={page>=Math.ceil(total/PER)} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronRight size={18}/></button>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
